import { useMemo, useState } from "react";
import { EmbedSignDocument } from "@documenso/embed-react";
import { useParams } from "react-router-dom";

import simpleFirmLogo from "@/assets/logo/new-design.png";
import { isValidSigningToken } from "@/Routes/publicSigningRoute.mjs";

import "./PublicSigningPage.css";

const DEFAULT_DOCUMENSO_HOST = "https://sign.simplefirm.com";
const configuredDocumensoHost =
  typeof process === "undefined" ? "" : process.env.REACT_APP_DOCUMENSO_URL;
const DOCUMENSO_BRAND_VARS = {
  background: "#ffffff",
  foreground: "#172b3b",
  muted: "#f2f5f7",
  mutedForeground: "#607080",
  popover: "#ffffff",
  popoverForeground: "#172b3b",
  card: "#ffffff",
  cardBorder: "#c6d0dc",
  cardBorderTint: "#e3e9ef",
  cardForeground: "#172b3b",
  fieldCard: "#fff7cc",
  fieldCardBorder: "#c62828",
  fieldCardForeground: "#7f1d1d",
  widget: "#f2f5f7",
  widgetForeground: "#19395f",
  border: "#c6d0dc",
  input: "#aebcca",
  primary: "#19395f",
  primaryForeground: "#ffffff",
  secondary: "#e3e9ef",
  secondaryForeground: "#19395f",
  accent: "#d7e0e8",
  accentForeground: "#19395f",
  destructive: "#9f2f2f",
  destructiveForeground: "#ffffff",
  ring: "#19395f",
  radius: "0rem",
  warning: "#bd7615",
};
// const DOCUMENSO_BRAND_CSS = `
//   body {
//     font-family: Inter, Arial, sans-serif;
//   }
// `;
const DOCUMENSO_BRAND_CSS = `
  body {
    font-family: Inter, Arial, sans-serif;
  }

  .field--FieldRootContainer[data-field-type='SIGNATURE'] {
    background-color: #fff7cc !important;
    border: 2px solid #c62828 !important;
  }

  .field--FieldRootContainer[data-field-type='SIGNATURE'] * {
    color: #7f1d1d;
  }

  .field--FieldRootContainer[data-field-type='SIGNATURE'][data-inserted='false'][data-readonly='false']::before {
    content: "SIGN";
    position: absolute;
    top: 50%;
    right: calc(100% + 2px);
    z-index: 30;
    min-width: 3.25rem;
    padding: 0.35rem 0.45rem;
    transform: translateY(-50%);
    border: 2px solid #c62828;
    border-right: 0;
    border-radius: 0.3rem 0 0 0.3rem;
    color: #7f1d1d;
    background: #fff7cc;
    box-shadow: 0 2px 5px rgba(127, 29, 29, 0.24);
    font-size: 0.75rem;
    font-weight: 800;
    line-height: 1.1;
    letter-spacing: 0.06em;
    text-align: center;
    pointer-events: none;
  }

  .embed--DocumentWidgetHeader p,
  .embed--DocumentWidgetContainer h3 span,
  .fixed.right-0.bottom-0.left-0 h2 + p {
    display: inline-block;
    margin-top: 0.25rem;
    padding: 0.35rem 0.65rem;
    border: 2px solid #c62828;
    border-radius: 0.375rem;
    color: #7f1d1d !important;
    background: #fff7cc;
    font-size: 1.05rem !important;
    font-weight: 700 !important;
    line-height: 1.25;
  }
`;

const normalizeHost = (value) => {
  try {
    const url = new URL(value || DEFAULT_DOCUMENSO_HOST);
    if (!["http:", "https:"].includes(url.protocol)) return DEFAULT_DOCUMENSO_HOST;
    return url.origin;
  } catch {
    return DEFAULT_DOCUMENSO_HOST;
  }
};

const signingErrorMessage = (error) => {
  if (typeof error === "string" && error.trim()) return error;
  return "We could not load this signature request. The link may be invalid or expired.";
};

const PublicSigningPage = () => {
  const { token = "" } = useParams();
  const [status, setStatus] = useState("loading");
  const [errorMessage, setErrorMessage] = useState("");
  const [embedKey, setEmbedKey] = useState(0);
  const documensoHost = useMemo(() => normalizeHost(configuredDocumensoHost), []);
  const directSigningUrl = useMemo(() => {
    if (!isValidSigningToken(token)) return "";
    return new URL(`/embed/sign/${encodeURIComponent(token)}`, documensoHost).toString();
  }, [documensoHost, token]);

  const retry = () => {
    setErrorMessage("");
    setStatus("loading");
    setEmbedKey((current) => current + 1);
  };

  if (!isValidSigningToken(token)) {
    return (
      <SigningShell>
        <StatusCard
          title="This signing link is not valid"
          message="Check that you opened the complete link from your email. If the problem continues, ask the sender for a new signature request."
          tone="error"
        />
      </SigningShell>
    );
  }

  if (status === "completed") {
    return (
      <SigningShell>
        <StatusCard
          title="Document signed"
          message="Thank you. Your completed document has been securely submitted. You may close this window."
          tone="success"
        />
      </SigningShell>
    );
  }

  if (status === "rejected") {
    return (
      <SigningShell>
        <StatusCard
          title="Document declined"
          message="Your response has been recorded. You may close this window."
          tone="neutral"
        />
      </SigningShell>
    );
  }

  return (
    <SigningShell compact>
      <section className="public-signing__workspace" aria-label="Document signing workspace">
        <div className="public-signing__workspace-heading">
          <div>
            <p className="public-signing__eyebrow">Secure electronic signature</p>
            <h1>Review and sign your document</h1>
          </div>
          <div className={`public-signing__status public-signing__status--${status}`} role="status">
            <span aria-hidden="true" />
            {status === "error"
              ? "Unable to load"
              : status === "ready"
                ? "Ready to sign"
                : "Loading document"}
          </div>
        </div>

        {status === "error" ? (
          <div className="public-signing__embed-error">
            <StatusCard
              title="We could not open the document"
              message={errorMessage}
              tone="error"
            />
            <div className="public-signing__error-actions">
              <button type="button" onClick={retry}>
                Try again
              </button>
              <a href={directSigningUrl} target="_blank" rel="noreferrer">
                Open signing page
              </a>
            </div>
          </div>
        ) : (
          <div className="public-signing__embed-wrap">
            {status === "loading" && (
              <div className="public-signing__loading" role="status">
                <span className="public-signing__spinner" aria-hidden="true" />
                Preparing your document…
              </div>
            )}
            <EmbedSignDocument
              key={embedKey}
              className="public-signing__embed"
              host={documensoHost}
              token={token}
              css={DOCUMENSO_BRAND_CSS}
              cssVars={DOCUMENSO_BRAND_VARS}
              darkModeDisabled
              language="en"
              onDocumentReady={() => setStatus("ready")}
              onDocumentCompleted={() => setStatus("completed")}
              onDocumentRejected={() => setStatus("rejected")}
              onDocumentError={(error) => {
                setErrorMessage(signingErrorMessage(error));
                setStatus("error");
              }}
            />
          </div>
        )}
      </section>
    </SigningShell>
  );
};

const SigningShell = ({ children, compact = false }) => (
  <main className={`public-signing ${compact ? "public-signing--compact" : ""}`}>
    <header className="public-signing__header">
      <img src={simpleFirmLogo} alt="SimpleFirm" />
      <div className="public-signing__security">
        <span aria-hidden="true">✓</span>
        Secure document signing
      </div>
    </header>
    <div className="public-signing__content">{children}</div>
    <footer className="public-signing__footer">
      <span>Your signing link is private. Do not forward it.</span>
      <span>© {new Date().getFullYear()} SimpleFirm</span>
    </footer>
  </main>
);

const StatusCard = ({ title, message, tone }) => (
  <section className={`public-signing__card public-signing__card--${tone}`} role="status">
    <span className="public-signing__card-icon" aria-hidden="true">
      {tone === "success" ? "✓" : tone === "error" ? "!" : "—"}
    </span>
    <h1>{title}</h1>
    <p>{message}</p>
  </section>
);

export default PublicSigningPage;
